import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getRoles, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { adminConfig, useResetOnNavigate, ActionsWrap, makeRoleHelpers, KernelPage } from '../kernelAdminShared';
import { PHONE_COUNTRY_CODES, DEFAULT_PHONE_PREFIX, splitPhone, combinePhone } from '../utils/phoneCodes';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';
import KernelSearchBox from '../components/KernelSearchBox';
import KernelHighlight from '../components/KernelHighlight';
import KernelPasswordField from '../components/KernelPasswordField';
import KernelLoginHistory from '../components/KernelLoginHistory';
import { fmtDateTime } from '../utils/kernelFormat';

const emptyForm = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  phone_mobile: '',
  password: '',
  confirm: '',
  company_id: '',
  role: '2',
  lang: 'it-IT',
};

function AdminUsers({ user, onLogout }) {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [officePrefix, setOfficePrefix] = useState(DEFAULT_PHONE_PREFIX);
  const [mobilePrefix, setMobilePrefix] = useState(DEFAULT_PHONE_PREFIX);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [warning, setWarning] = useState('');
  const { roleName, roleOptions } = makeRoleHelpers(t, roles);
  const [search, setSearch] = useState('');
  const [historyUser, setHistoryUser] = useState(null);

  // Ricerca: più parole separate da spazio, devono comparire tutte (AND), nel testo che si vede in tabella.
  const terms = searchTerms(search);
  const visibleUsers = users.filter((u) => matchesAllTerms(
    [`${u.name} ${u.surname || ''}`, u.email, u.company_name || '', roleName(u.role), Number(u.status) === 4 ? t('adminUsers.inactive') : ''].join(' '),
    terms
  ));

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const [data, rolesData] = await Promise.all([getUsers(true), adminConfig.dynamicRoles ? getRoles() : null]);
      if (rolesData) setRoles(rolesData.roles || []);
      setUsers(data.users || []);
      setCompanies(data.companies || []);
    } catch (err) {
      setError(errorMessage(err, t('adminUsers.errorLoad')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetMessages = () => {
    setError('');
    setSuccess('');
    setWarning('');
  };

  useResetOnNavigate(() => {
    setShowForm(false);
    setEditId(null);
  });

  const startCreate = () => {
    resetMessages();
    const firstCompany = companies.find((c) => c.id === 1) || companies[0];
    setForm({
      ...emptyForm,
      company_id: firstCompany ? String(firstCompany.id) : '',
      lang: firstCompany?.default_lang || 'it-IT',
    });
    setOfficePrefix(DEFAULT_PHONE_PREFIX);
    setMobilePrefix(DEFAULT_PHONE_PREFIX);
    setEditId(null);
    setShowForm(true);
  };

  const startEdit = (u) => {
    resetMessages();
    const office = splitPhone(u.phone);
    const mobile = splitPhone(u.phone_mobile);
    setForm({
      name: u.name || '',
      surname: u.surname || '',
      email: u.email || '',
      phone: office.number,
      phone_mobile: mobile.number,
      password: '',
      confirm: '',
      company_id: u.company_id != null ? String(u.company_id) : '',
      role: String(u.role || '2'),
      lang: u.lang || 'it-IT',
    });
    setOfficePrefix(office.prefix);
    setMobilePrefix(mobile.prefix);
    setEditId(u.id);
    setShowForm(true);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(t('adminUsers.deleteConfirm', { name: `${u.name} ${u.surname || ''}`.trim() }))) {
      return;
    }
    resetMessages();
    try {
      const result = await deleteUser(u.id);
      setSuccess(t('adminUsers.deleted'));
      const clearedProjects = result?.cleared_default_assignee_projects || [];
      if (adminConfig.deleteWarning && clearedProjects.length > 0) {
        setWarning(t('companyUsers.deactivatedClearedAssignee', { projects: clearedProjects.map((p) => p.name).join(', ') }));
      }
      fetchUsers();
    } catch (err) {
      setError(errorMessage(err, t('adminUsers.errorDelete')));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!form.name.trim()) {
      setError(t('adminUsers.errorName'));
      return;
    }
    if (!form.company_id) {
      setError(t('adminUsers.errorCompany'));
      return;
    }
    if (!form.email.trim()) {
      setError(t('adminUsers.errorEmail'));
      return;
    }
    if (!editId) {
      if (form.password.length < 8) {
        setError(t('adminUsers.errorPassword'));
        return;
      }
      if (form.password !== form.confirm) {
        setError(t('adminUsers.errorMismatch'));
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim().toLowerCase(),
        phone: combinePhone(officePrefix, form.phone),
        phone_mobile: combinePhone(mobilePrefix, form.phone_mobile),
        company_id: Number(form.company_id),
        role: Number(form.role),
        lang: form.lang,
      };
      if (editId) {
        await updateUser(editId, payload);
        setSuccess(t('adminUsers.updated'));
      } else {
        payload.password = form.password;
        await createUser(payload);
        setSuccess(t('adminUsers.created'));
      }
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setError(errorMessage(err, t('adminUsers.errorSave')));
    } finally {
      setSaving(false);
    }
  };

  const isSupportoGroup = Number(form.company_id) === 990001;
  const requiredEmpty = (v) => (v === '' || v == null ? ' required-empty' : '');

  if (historyUser) {
    return (
      <KernelPage user={user} onLogout={onLogout}>
        <KernelLoginHistory person={historyUser} currentUserId={user?.id} onClose={() => setHistoryUser(null)} />
      </KernelPage>
    );
  }

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('adminUsers.title')}</h1>
          <p className="page-subtitle">{t('adminUsers.subtitle')}</p>
        </div>
        {!showForm && (
          <button className="btn" type="button" onClick={startCreate}>
            {t('adminUsers.newUser')}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {warning && <div className="warning-message">{warning}</div>}

      {loading && <div className="loading">{t('common.loading')}</div>}

      {!loading && !showForm && (
        <>
        <div className="ksb-bar">
          <KernelSearchBox value={search} onChange={setSearch} placeholder={t('adminUsers.search')} title={t('adminUsers.searchHint')} />
        </div>
        <div className="card">
          <div className="card-body table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('adminUsers.name')}</th>
                  <th>{t('adminUsers.email')}</th>
                  <th>{t('adminUsers.company')}</th>
                  <th>{t('adminUsers.role')}</th>
                  <th>{t('loginHistory.lastLogin')}</th>
                  <th className="actions">{t('adminUsers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => (
                  <tr key={u.id} className={Number(u.status) === 4 ? 'row-inactive' : ''}>
                    <td>
                      <KernelHighlight text={`${u.name} ${u.surname || ''}`.trim()} terms={terms} />
                      {Number(u.status) === 4 && (
                        <span className="badge badge-warning"><KernelHighlight text={t('adminUsers.inactive')} terms={terms} /></span>
                      )}
                    </td>
                    <td><KernelHighlight text={u.email} terms={terms} /></td>
                    <td>{u.company_name ? <KernelHighlight text={u.company_name} terms={terms} /> : '—'}</td>
                    <td><KernelHighlight text={roleName(u.role)} terms={terms} /></td>
                    <td className="nowrap">{u.last_login_at ? fmtDateTime(u.last_login_at) : <span style={{ opacity: 0.6 }}>{t('loginHistory.never')}</span>}</td>
                    <td className="actions">
                      <ActionsWrap>
                        <button
                          type="button"
                          className="icon-btn"
                          title={t('loginHistory.show')}
                          onClick={() => setHistoryUser(u)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title={t('adminUsers.edit')}
                          onClick={() => startEdit(u)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          title={t('adminUsers.delete')}
                          onClick={() => handleDelete(u)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </ActionsWrap>
                    </td>
                  </tr>
                ))}
                {visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      {users.length === 0 ? t('adminUsers.empty') : t('adminUsers.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {!loading && showForm && (
        <div className="card">
          <div className="card-body">
            <h2 className="form-section-title">
              {editId ? t('adminUsers.editTitle') : t('adminUsers.newTitle')}
            </h2>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row-2">
                <div className={`form-group${requiredEmpty(form.name)}`}>
                  <label>{t('adminUsers.name')} *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('adminUsers.surname')}</label>
                  <input
                    type="text"
                    value={form.surname}
                    onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  />
                </div>
              </div>

              <div className={`form-group${requiredEmpty(form.email)}`}>
                <label>{t('adminUsers.email')} *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('adminUsers.phone')}</label>
                  <div className="phone-input-group">
                    <select value={officePrefix} onChange={(e) => setOfficePrefix(e.target.value)}>
                      {PHONE_COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('adminUsers.phoneMobile')}</label>
                  <div className="phone-input-group">
                    <select value={mobilePrefix} onChange={(e) => setMobilePrefix(e.target.value)}>
                      {PHONE_COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={form.phone_mobile}
                      onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>{t('adminUsers.lang')}</label>
                <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}>
                  <option value="it-IT">{t('languages.it-IT')}</option>
                  <option value="en-GB">{t('languages.en-GB')}</option>
                </select>
              </div>

              {!editId && (
                <div className="form-row-2">
                  <KernelPasswordField
                    label={`${t('adminUsers.password')} *`}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password"
                  />
                  <KernelPasswordField
                    label={`${t('adminUsers.confirm')} *`}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              )}

              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('adminUsers.company')} *</label>
                  <select
                    value={form.company_id}
                    onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                  >
                    <option value="">{t('adminUsers.selectCompany')}</option>
                    {companies.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('adminUsers.role')} *</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    {roleOptions(true).map((r) => (
                      <option key={r.id} value={String(r.id)}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {Number(form.role) === 1 && !isSupportoGroup && (
                <div className="warning-message">{t('adminUsers.roleSupportoWarn')}</div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </KernelPage>
  );
}

export default AdminUsers;
